import React, { useContext } from 'react';
import { Icon, Label } from 'semantic-ui-react';

import useCopyToClipboard from '@src/hooks/CopyToClipboard';
import { getDaysString } from '@src/lib/date';
import config from '@config/config';
import { Segment } from '@modules/common/components/Segment';
import { AccordionItem } from '@modules/common/components/Accordion';
import {
  StyledActions,
  StyledBasicInfo,
  StyledClientActions,
  StyledClientId,
  StyledClientInfo,
  StyledDropdownIcon,
  StyledNameAndId,
  StyledTitle,
} from '@modules/auth/widgets/OAuthClient/ExistingClients/style';

import { OAuthClientDescriptor } from '../../../components/types/oauth-client';
import EditClient from '../EditClient';
import DeletedClient from './DeletedClient';
import DeleteOauthClient from './DeleteOAuthClient';
import { AuthenticationContext } from '../../../components/context';
import SPAInfo from './SPAInfo';

type Props = {
  client: OAuthClientDescriptor;
  activeIndex: number;
  setActiveIndex: (index: number) => void;
  index: number;
  onClientUpdated?: () => void;
  editable?: boolean;
};

function Client({ client, activeIndex, setActiveIndex, index, editable, onClientUpdated }: Props) {
  const { isCopied, copyToClipboard } = useCopyToClipboard();

  if (client.isDeleted) {
    return <DeletedClient client={client} />;
  }

  const expiryDate = client.expiryDate;
  const readonly = useContext(AuthenticationContext).readonly;
  return (
    <AccordionItem
      active={activeIndex === index}
      index={index}
      onClick={() => {
        if (activeIndex === index) {
          setActiveIndex(-1);
        } else {
          setActiveIndex(index);
        }
      }}
      key={client.clientId}
      title={
        <StyledClientInfo>
          <StyledBasicInfo>
            <StyledDropdownIcon
              name={activeIndex === index ? 'chevron circle down' : 'chevron circle right'}
            />
            <StyledNameAndId>
              <StyledTitle>{client.name}</StyledTitle>
              <div>
                <span>ID:</span> <StyledClientId>{client.clientId}</StyledClientId>
                <Icon
                  onClick={(e: React.MouseEvent) => {
                    e.stopPropagation();
                    copyToClipboard(client.clientId);
                  }}
                  name={isCopied ? 'thumbs up' : 'copy'}
                  color={isCopied ? 'green' : undefined}
                  style={{ cursor: 'pointer', marginLeft: '0.75rem' }}
                />
              </div>
            </StyledNameAndId>
          </StyledBasicInfo>
          <StyledActions>
            {editable && onClientUpdated && !readonly && (
              <StyledClientActions>
                <EditClient client={client} onClientUpdated={onClientUpdated} />
                <DeleteOauthClient clientId={client.clientId} deleteClient={onClientUpdated} />
              </StyledClientActions>
            )}
          </StyledActions>
        </StyledClientInfo>
      }
      content={
        <Segment secondary style={{ borderRadius: 0 }}>
          <div>
            <b>Supported flow:</b>{' '}
            <Label basic>
              {client.parameters.supportedFlows && client.parameters.supportedFlows.length > 0
                ? client.parameters.supportedFlows[0]
                : 'None'}
            </Label>
          </div>
          {config.oAuthClientsExpiryEnabled && expiryDate && (
            <div>
              <b>Expiry date</b>: {expiryDate.format('DD MMMM YYYY, HH:mm (UTC)')}{' '}
              {getDaysString(expiryDate.toDate())}
            </div>
          )}
          <SPAInfo client={client} />
        </Segment>
      }
    />
  );
}

export default Client;
